import { ScreeningAnswers } from '../types';

/**
 * Qualification Rules based on Legal Ops criteria (Dec 2024)
 *
 * Key concept: "Override triggers" - certain sub-question Yes answers can
 * override Unknown on parent categories, allowing the case to qualify.
 *
 * QUALIFIED: ALL 5 parent categories are "Yes" (or Unk with override)
 * ESCALATE: ANY parent is "No" OR (ANY parent is "Unk" AND no child overrides)
 */

/**
 * The 5 parent category question IDs
 */
export const PARENT_QUESTIONS = [
  { key: 'liability_assigned', label: 'Liability' },
  { key: 'defendant_insured', label: 'Coverage' },
  { key: 'injured', label: 'Damages' },
  { key: 'sought_treatment', label: 'Treatment' },
  { key: 'no_current_lawyer', label: 'Representation' },
];

/**
 * Coverage A override triggers (defendant insured sub-questions)
 * If ANY of these are "Yes" → can override Unk on other parents
 */
export const COVERAGE_A_OVERRIDES = [
  'commercial_policy',
  'uber_lyft',
  'govt_vehicle',
];

/**
 * Coverage B questions (client's own insurance)
 * Note: Only um_uim (UM/UIM benefits) is required to satisfy Coverage B.
 * client_auto_insurance is informational but not required.
 */
export const COVERAGE_B_QUESTIONS = ['client_auto_insurance', 'um_uim'];

/**
 * The key question for Coverage B qualification
 */
export const COVERAGE_B_KEY_QUESTION = 'um_uim';

/**
 * Damages override triggers
 * If ANY of these are "Yes" → can override Unk on other parents
 */
export const DAMAGES_OVERRIDES = [
  'broken_bones',
  'hospital_stay',
  'surgery',
  'head_trauma',
];

/**
 * Check if any Coverage A sub-question has "yes" answer.
 * Triggers override ability for other parent categories.
 */
export const hasCoverageAOverride = (answers: ScreeningAnswers): boolean => {
  return COVERAGE_A_OVERRIDES.some((key) => answers[key] === 'yes');
};

/**
 * Check if Coverage B (UM/UIM benefits) has "yes" answer.
 * This satisfies coverage when defendant is not insured.
 */
export const hasCoverageBOverride = (answers: ScreeningAnswers): boolean => {
  return answers[COVERAGE_B_KEY_QUESTION] === 'yes';
};

/**
 * Check if Coverage B is explicitly "no" (disqualifying condition).
 * When defendant is not insured AND client has no UM/UIM benefits → disqualified.
 */
export const isCoverageBDisqualified = (answers: ScreeningAnswers): boolean => {
  return answers[COVERAGE_B_KEY_QUESTION] === 'no';
};

/**
 * Check if any Damages sub-question has "yes" answer.
 * Triggers override ability for other parent categories.
 */
export const hasDamagesOverride = (answers: ScreeningAnswers): boolean => {
  return DAMAGES_OVERRIDES.some((key) => answers[key] === 'yes');
};

/**
 * Check if any override condition is met.
 * Returns true if Unknown on parent categories can be overridden.
 */
export const hasAnyOverride = (answers: ScreeningAnswers): boolean => {
  return (
    hasCoverageAOverride(answers) ||
    hasCoverageBOverride(answers) ||
    hasDamagesOverride(answers)
  );
};

/**
 * Check if coverage is satisfied.
 * Coverage is satisfied if:
 * - Defendant is insured (Yes), OR
 * - Client has UM/UIM benefits (Yes)
 */
export const hasCoverage = (answers: ScreeningAnswers): boolean => {
  if (answers.defendant_insured === 'yes') return true;
  return hasCoverageBOverride(answers);
};

/**
 * Check if coverage is disqualified (hard reject).
 * Coverage is disqualified if:
 * - Defendant is NOT insured (No or Unknown), AND
 * - Client does NOT have UM/UIM benefits (No)
 */
export const isCoverageDisqualified = (answers: ScreeningAnswers): boolean => {
  const defendantNotInsured =
    answers.defendant_insured === 'no' ||
    answers.defendant_insured === 'unknown';
  return defendantNotInsured && isCoverageBDisqualified(answers);
};

/**
 * Get the effective answer for a parent question, considering overrides.
 * If answer is "unknown" but override conditions are met, treat as "yes".
 *
 * Special handling for Coverage (defendant_insured):
 * - If defendant not insured (No) → always 'no' (escalate, regardless of UM/UIM)
 * - If unknown but override triggers (broken bones, etc.) → 'yes'
 */
export const getEffectiveAnswer = (
  answers: ScreeningAnswers,
  questionKey: string,
): 'yes' | 'no' | 'unknown' => {
  const answer = answers[questionKey];

  // Special handling for Coverage category
  if (questionKey === 'defendant_insured') {
    if (answer === 'yes') return 'yes';

    // Defendant explicitly not insured → always escalate (no UM/UIM override)
    if (answer === 'no') return 'no';

    // Unknown - check if override triggers apply (broken bones, etc.)
    if (hasAnyOverride(answers)) {
      return 'yes';
    }
    return 'unknown';
  }

  // Standard handling for other parent questions
  if (answer === 'yes') return 'yes';
  if (answer === 'no') return 'no';

  // For unknown answers, check if overrides apply
  if (hasAnyOverride(answers)) {
    return 'yes'; // Override Unk to Yes
  }
  return 'unknown';
};

/**
 * Get escalation reasons based on answer state.
 * Returns array of reasons why the case needs escalation.
 * Coverage is now included in escalation (defendant not insured → escalate).
 */
export const getEscalationReasons = (answers: ScreeningAnswers): string[] => {
  const reasons: string[] = [];

  PARENT_QUESTIONS.forEach(({ key, label }) => {
    const effectiveAnswer = getEffectiveAnswer(answers, key);

    if (effectiveAnswer === 'no') {
      reasons.push(`${label}: No`);
    } else if (effectiveAnswer === 'unknown') {
      reasons.push(`${label}: Unknown (no override)`);
    }
  });

  return reasons;
};
